// Client for the white-label tenant portal endpoints of the lawyer backend.
// Same conventions as the other clients here: REACT_APP_API_URL as the base,
// a Firebase ID token as Bearer auth, and FastAPI's `detail` as the message.
//
// Nearly all of these are read-only. A tenant can see its own cases and download
// the finished documents; advancing a case stays with the lawyer. The one write
// is recording a payment the partner took themselves (customer-pays partners,
// lawyer-backend tenant_portal/offline_payments.py).

import { ApiError, errorMessageFrom } from './client';

export interface TenantCase {
  id: string;
  service?: string;
  status?: string;
  lawyer_status?: string;
  created_at?: any;
  completed_at?: any;
  // When the case reached the state its label shows; null when finished.
  status_since?: any;
  user_email?: string;
  user_display_name?: string;
  /** The client's case has a payment on it (card, or one the partner recorded and the client used). */
  paid?: boolean;
  /** A payment the partner recorded: recorded (client not back yet), settled (used), revoked. */
  offline_payment_status?: 'recorded' | 'settled' | 'revoked';
}

export interface TenantFinalDocument {
  id: string;
  fileName: string | null;
  uploadedAt?: any;
}

export interface TenantCaseDetail extends TenantCase {
  final_documents: TenantFinalDocument[];
}

export interface TenantCaseList {
  tenantId: string;
  tenantName: string;
  cases: TenantCase[];
}

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;

const get = async (token: string, path: string, fallbackError: string): Promise<any> => {
  const response = await fetch(apiUrl(path), {
    headers: { Authorization: `Bearer ${token}` },
  });
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    throw new ApiError(errorMessageFrom(data, fallbackError), response.status, data);
  }
  return data;
};

const send = async (token: string, path: string, method: string, fallbackError: string, body?: unknown): Promise<any> => {
  const response = await fetch(apiUrl(path), {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    throw new ApiError(errorMessageFrom(data, fallbackError), response.status, data);
  }
  return data;
};

export const fetchTenantCases = (token: string): Promise<TenantCaseList> =>
  get(token, '/tenant/cases', 'Could not load your cases.');

export const fetchTenantCase = (token: string, caseId: string): Promise<TenantCaseDetail> =>
  get(token, `/tenant/cases/${encodeURIComponent(caseId)}`, 'Could not load this case.');

export const fetchTenantDocumentUrl = async (
  token: string,
  caseId: string,
  documentName: string
): Promise<string> => {
  const data = await get(
    token,
    `/tenant/cases/${encodeURIComponent(caseId)}/final-documents/${encodeURIComponent(
      documentName
    )}/signed-url`,
    'Could not prepare that download.'
  );
  return data.url;
};

/** The client paid the partner directly: their case continues and Coepi invoices the partner. */
export const recordOfflinePayment = (token: string, caseId: string, note?: string): Promise<any> =>
  send(token, `/tenant/cases/${encodeURIComponent(caseId)}/offline-payment`, 'POST', 'Could not mark this client as paid.', {
    note: note || null,
  });

/** Take it back while the client has not continued yet. */
export const withdrawOfflinePayment = (token: string, caseId: string): Promise<any> =>
  send(token, `/tenant/cases/${encodeURIComponent(caseId)}/offline-payment`, 'DELETE', 'Could not undo this payment.');
